import {
    BodyTextMedium,
    FormInputCheckbox,
    Link,
    pxToRem,
    SkeletonPlaceholder,
} from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import React, { FC } from 'react';
import { Consents } from 'src/modules/auth/services/ClickwrapService';
import styled from '@emotion/styled';
import { ConsentPageTestId } from 'src/modules/auth/screens/testIds';

export interface ExtendedConsent extends Consents {
    isChecked: boolean;
}

const ConsentSkeletonLoader = () => {
    const checkbox = (
        <ConsentContainer>
            <SkeletonPlaceholder width={25} height={25} marginRight={10} />
            <SkeletonPlaceholder width="90%" height={25} />
        </ConsentContainer>
    );
    return (
        <>
            <SkeletonPlaceholder width="100%" height={72} marginBottom={24} />
            {checkbox}
            {checkbox}
            {checkbox}
        </>
    );
};

interface ConsentCheckboxProps {
    fetchingConsents: boolean;
    consents: ExtendedConsent[];
}
export const ConsentCheckbox: FC<ConsentCheckboxProps> = ({ fetchingConsents, consents }) => {
    const { t } = useTranslation();
    return (
        <CheckboxContainer data-testid={ConsentPageTestId.ClickwrapConsentContainer}>
            {fetchingConsents && <ConsentSkeletonLoader />}
            {consents.map((consent, index) => (
                <ConsentContainer key={consent.key}>
                    <FormInputCheckbox
                        name={`consents.${index}.isChecked`}
                        label={`${consent.acceptanceLanguage} ${consent.url ? consent.title : ''}`}
                    />
                    {consent.url && (
                        <BodyTextMedium as="span" css={{ display: 'inline' }} fontColor="blue55">
                            <ConsentLink key={`link_${consent.key}`} inheritStyling>
                                <a href={consent.url} target="_blank" rel="noreferrer noopener">
                                    ({t('auth:consent_form_page.see_details')})
                                </a>
                            </ConsentLink>
                        </BodyTextMedium>
                    )}
                </ConsentContainer>
            ))}
        </CheckboxContainer>
    );
};

const CheckboxContainer = styled.div({
    width: '100%',
    paddingBottom: '40px',
});

const ConsentContainer = styled.div({
    marginBottom: pxToRem(12),
});

const ConsentLink = styled(Link)({
    marginLeft: pxToRem(35),
    '&:hover': {
        marginLeft: pxToRem(35),
    },
});
